// src/pages/Campaigns.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCampaigns,
  deleteCampaign,
  startCampaign,
  createCampaign,
  addCampaignRecipients,
  addCampaignMedia,
  getConnections,
  uploadCampaignRecipients,
} from '../api';

const statusColors = {
  draft: 'bg-gray-400',
  scheduled: 'bg-yellow-500',
  processing: 'bg-blue-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500',
  paused: 'bg-orange-500',
};

const statusIcons = {
  draft: '📝',
  scheduled: '⏰',
  processing: '⚡',
  completed: '✅',
  failed: '❌',
  paused: '⏸️',
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await getCampaigns();
      if (res.status === 1) setCampaigns(res.data || []);
    } catch (e) {
      console.error('Failed to fetch campaigns', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleStart = async (id) => {
    if (!window.confirm('Start this campaign?')) return;
    try {
      await startCampaign(id);
      fetchCampaigns();
    } catch (e) {
      alert('Failed to start campaign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await deleteCampaign(id);
      fetchCampaigns();
    } catch (e) {
      alert('Delete failed');
    }
  };

  const filteredCampaigns = campaigns.filter((c) => {
    const nameMatch = c.name.toLowerCase().includes(filters.search.toLowerCase());
    const statusMatch = filters.status === 'all' || c.status === filters.status;
    const dateMatch =
      (!filters.dateFrom || new Date(c.created_at) >= new Date(filters.dateFrom)) &&
      (!filters.dateTo || new Date(c.created_at) <= new Date(filters.dateTo));
    return nameMatch && statusMatch && dateMatch;
  });

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 pt-3 pb-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-gray-900">📋 Campaigns</h1>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {campaigns.length}
          </span>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-sm transition"
        >
          + New
        </button>
      </div>

      {loading && <div className="text-center text-gray-500 py-4 text-sm">Loading campaigns…</div>}

      {/* Filter Bar – equal widths */}
      {!loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 mb-5 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                Search
              </label>
              <input
                type="text"
                placeholder="Campaign name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white transition"
              >
                <option value="all">All</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
            </div>
            <div className="flex justify-end items-end">
              <button
                onClick={() => setFilters({ search: '', status: 'all', dateFrom: '', dateTo: '' })}
                className="text-sm text-blue-500 hover:text-blue-700 font-medium transition hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCampaigns.map((c) => (
          <div
            key={c.id}
            onClick={() => navigate(`/campaigns/${c.id}`)}
            className="bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition cursor-pointer p-4 flex flex-col"
          >
            <div className="flex justify-between items-start mb-1.5">
              <h3 className="text-base font-semibold text-gray-800 truncate">{c.name}</h3>
              <span className={`text-xs font-medium text-white px-2 py-0.5 rounded-full ${statusColors[c.status] || 'bg-gray-400'}`}>
                {statusIcons[c.status]} {c.status}
              </span>
            </div>
            <p className="text-gray-600 text-sm mb-2 line-clamp-2 leading-relaxed">
              {c.message_text?.substring(0, 70)}
              {c.message_text?.length > 70 ? '…' : ''}
            </p>

            <div className="flex flex-wrap gap-2 text-xs mt-1">
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Total</span>
                <span className="font-bold">{c.total || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Sent</span>
                <span className="font-bold text-green-600">{c.sent || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Failed</span>
                <span className="font-bold text-red-600">{c.failed || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Pending</span>
                <span className="font-bold text-yellow-600">{c.pending || 0}</span>
              </div>
            </div>

            <div className="text-[11px] text-gray-400 mt-1.5 flex gap-2">
              <span>⏱ {c.delay_seconds}s</span>
              {c.scheduled_at && <span>📅 {new Date(c.scheduled_at).toLocaleDateString()}</span>}
            </div>

            <div className="mt-3 pt-2 border-t border-gray-100 flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
              {c.status === 'draft' || c.status === 'scheduled' ? (
                <button
                  onClick={() => handleStart(c.id)}
                  className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg transition"
                >
                  ▶ Start
                </button>
              ) : c.status === 'processing' ? (
                <span className="text-xs text-blue-600 font-medium">⏳ Running</span>
              ) : null}
              <button
                onClick={() => handleDelete(c.id)}
                className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg transition"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCampaigns.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-4xl mb-2">📭</div>
          <p className="text-lg font-semibold text-gray-700">No campaigns match your filters</p>
          <p className="text-sm text-gray-500">Try adjusting the search or filter criteria</p>
        </div>
      )}

      {/* Create Campaign Modal – improved compact version */}
      {showCreateModal && <CreateCampaignModal onClose={() => setShowCreateModal(false)} onSuccess={() => { setShowCreateModal(false); fetchCampaigns(); }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREATE CAMPAIGN MODAL (Fully compact – no scroll on 80%+)
// ─────────────────────────────────────────────────────────────
function CreateCampaignModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [campaignId, setCampaignId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [fromMobile, setFromMobile] = useState('');

  const [name, setName] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [delaySeconds, setDelaySeconds] = useState(2);
  const [messageText, setMessageText] = useState('');
  const [media, setMedia] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const res = await getConnections();
        if (res.status === 1) {
          const conns = res.data || [];
          setConnections(conns);
          const connected = conns.find(c => c.status === 'connected');
          if (connected) {
            setSelectedConnectionId(connected.id);
            setFromMobile(connected.phone_number || '');
          } else if (conns.length > 0) {
            setSelectedConnectionId(conns[0].id);
            setFromMobile(conns[0].phone_number || '');
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchConnections();
  }, []);

  useEffect(() => {
    const conn = connections.find(c => c.id === selectedConnectionId);
    if (conn) {
      setFromMobile(conn.phone_number || '');
    }
  }, [selectedConnectionId, connections]);

  const addMedia = () => {
    setMedia([...media, { media_url: '', media_type: 'image', caption: '', sort_order: media.length }]);
  };
  const updateMedia = (idx, field, value) => {
    const updated = [...media];
    updated[idx][field] = value;
    setMedia(updated);
  };
  const removeMedia = (idx) => setMedia(media.filter((_, i) => i !== idx));

  const parseRecipients = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split('\t').map(h => h.trim().toLowerCase());
    return lines.slice(1).map(line => {
      const cols = line.split('\t').map(c => c.trim());
      const obj = {};
      headers.forEach((h, i) => {
        if (h === 'lead_name' || h === 'mobile') {
          obj[h] = cols[i] || '';
        }
      });
      return obj;
    }).filter(r => r.mobile);
  };

  const handleRecipientInput = (e) => {
    setRecipientInput(e.target.value);
    const parsed = parseRecipients(e.target.value);
    setRecipients(parsed);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) return alert('Please select a file');
    if (!fromMobile) return alert('Please select a device with a phone number');
    setUploading(true);
    try {
      const res = await uploadCampaignRecipients(campaignId, fromMobile, uploadFile);
      alert(res.description || 'Upload successful');
      setUploadFile(null);
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.description || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!fromMobile) {
      alert('Please select a device with a phone number.');
      return;
    }
    setLoading(true);
    try {
      const res = await createCampaign({
        name,
        scheduled_at: scheduledAt || null,
        delay_seconds: parseInt(delaySeconds, 10) ,
        message_text: messageText,
      });
      if (res.status === 1) {
        setCampaignId(res.data.id);
        setStep(2);
      } else {
        alert(res.description);
      }
    } catch (e) {
      alert('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    if (!fromMobile) {
      alert('Please select a device with a phone number.');
      return;
    }
    setLoading(true);
    try {
      for (const m of media) {
        if (m.media_url.trim()) {
          await addCampaignMedia(campaignId, {
            media_url: m.media_url,
            media_type: m.media_type,
            caption: m.caption,
            sort_order: m.sort_order,
          });
        }
      }
      if (recipients.length > 0) {
        const formatted = recipients.map(r => ({
          lead_name: r.lead_name || '',
          mobile: r.mobile || '',
        }));
        await addCampaignRecipients(campaignId, fromMobile, formatted);
      }
      onSuccess();
    } catch (e) {
      alert('Failed to add media/recipients: ' + (e.response?.data?.description || e.message));
    } finally {
      setLoading(false);
    }
  };

  // ─── Compact Modal UI ──────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <span className="text-xl">📨</span>
            <h3 className="text-lg font-semibold text-gray-900">Create Campaign</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none transition">
            ✕
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-4 pt-3 pb-1.5">
          <div className="flex items-center justify-between relative">
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-gray-200 z-0"></div>
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-green-500 z-0 transition-all duration-300"
              style={{ width: step === 1 ? '50%' : '100%' }}
            ></div>
            <div className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-[10px] font-bold shadow-md">
              1
            </div>
            <div className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-600 text-[10px] font-bold shadow-md">
              2
            </div>
          </div>
          <div className="flex justify-between text-[10px] font-medium text-gray-500 mt-0.5">
            <span>Details</span>
            <span>Media & Recipients</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="px-4 py-2">
          {step === 1 ? (
            <form onSubmit={handleStep1} className="space-y-2.5">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Campaign Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Summer Sale 2026"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Send from device</label>
                <select
                  value={selectedConnectionId}
                  onChange={(e) => setSelectedConnectionId(Number(e.target.value))}
                  className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white text-sm transition"
                >
                  {connections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.phone_number || 'Unnamed'} ({c.phone_number || 'No phone'})
                    </option>
                  ))}
                </select>
                {fromMobile && (
                  <p className="text-xs text-gray-500 mt-0.5">📱 Sender: {fromMobile}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Scheduled Start</label>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm transition"
                />
                <p className="text-xs text-gray-400 mt-0.5">Leave empty to start immediately.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Delay (seconds)</label>
                <input
                  type="number"
                  min="0"
                  value={delaySeconds}
                  onChange={(e) => setDelaySeconds(e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Message Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows="3"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="w-full mt-0.5 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm transition"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {loading ? 'Creating…' : 'Next →'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleStep2} className="space-y-2.5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Media (optional)</label>
                {media.map((m, idx) => (
                  <div key={idx} className="flex flex-wrap gap-1 items-center mt-1.5">
                    <input
                      type="text"
                      placeholder="URL"
                      value={m.media_url}
                      onChange={(e) => updateMedia(idx, 'media_url', e.target.value)}
                      className="flex-1 min-w-[80px] px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                    />
                    <select
                      value={m.media_type}
                      onChange={(e) => updateMedia(idx, 'media_type', e.target.value)}
                      className="px-2 py-1.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 text-sm"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="document">Document</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Caption"
                      value={m.caption}
                      onChange={(e) => updateMedia(idx, 'caption', e.target.value)}
                      className="flex-1 min-w-[60px] px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeMedia(idx)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addMedia}
                  className="text-sm text-green-600 hover:text-green-800 font-medium mt-1"
                >
                  + Add Media
                </button>
              </div>

              <div className="border-t border-gray-100 pt-2.5 mt-2">
                <label className="block text-sm font-medium text-gray-700">Upload Excel</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!uploadFile || uploading}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
                {uploadFile && (
                  <p className="text-xs text-gray-400 mt-1">📄 {uploadFile.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Or paste recipients</label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Tab‑separated: <code className="bg-gray-100 px-1 py-0.5 rounded">lead_name &nbsp; mobile</code>
                </p>
                <textarea
                  rows="3"
                  placeholder="Paste your recipient list here..."
                  value={recipientInput}
                  onChange={handleRecipientInput}
                  className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                />
                {recipients.length > 0 && (
                  <div className="mt-1 bg-green-50 border border-green-200 rounded-lg p-2 text-sm flex items-center gap-2">
                    <span className="font-medium">✅ {recipients.length} parsed</span>
                    <span className="text-gray-500">First: {recipients[0]?.mobile}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {loading ? 'Saving…' : 'Finish'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}